import { useState, useEffect } from 'react'
import s from './ProposalTemplates.scss'
import { CreateProposalModal } from './CreateProposalModal'
import { InstallPredefinedTemplateModal } from './InstallPredefinedTemplateModal'
import {
  makePostRequest,
  makeGetRequest,
  makeDeleteRequest,
  makePutRequest,
  getBaseUrl
} from 'utils/api'
import { List } from './List'
import { useDispatch } from 'react-redux'
import { PUSH_NOTIFICATION } from 'actions/actionTypes'
import update from 'immutability-helper'
import { withSweetAlert } from 'hocs'
import { useLoading } from 'hooks'
import { AddButton } from 'components/common/AddButton'

const ProposalTemplates = () => {
  const reduxDispatch = useDispatch()
  const [isCreateProposalModalVisible, setIsCreateProposalModalVisible] = useState(false)
  const [templates, setTemplates] = useState([])
  const [predefinedTemplates, setPredefinedTemplates] = useState([])
  const [isEditedTemplate, setIsEditedTemplate] = useState(null)
  const [selectedPrefinedTemplate, setSelectedPrefinedTemplate] = useState(null)
  const [, withLoadingLocks] = useLoading()

  const saveTemplate = async (template, title) => {
    const payload = {
      content: template,
      title
    }
    let response
    try {
      if (isEditedTemplate) {
        response = await makePutRequest(`/v1/proposaltemplates/${isEditedTemplate.id}`, payload)
        const index = templates.findIndex(t => t.id === response.id)
        const newState = update(templates, {
          [index]: {
            $set: response
          }
        })
        setTemplates(newState)
      } else {
        response = await makePostRequest('/v1/proposaltemplates', payload)
        setTemplates([...templates, response])
      }
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'Template saved successfuly',
          level: 'success'
        }
      })
    } catch (e) {
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'There was a problem saving template',
          level: 'error'
        }
      })
    }
    setIsCreateProposalModalVisible(false)
    setIsEditedTemplate(null)
  }

  useEffect(() => {
    ;(async () => {
      const results = await makeGetRequest('/v1/proposaltemplates')
      setTemplates(results)
      const templates = await makeGetRequest('/v1/predesignedproposaltemplates')
      setPredefinedTemplates(templates)
    })()
  }, [])

  const openEditModal = async template => {
    try {
      const { content } = await withLoadingLocks(
        makeGetRequest(`/v1/proposaltemplates/${template.id}`)
      )
      setIsEditedTemplate({ ...template, content })
      setIsCreateProposalModalVisible(true)
    } catch (e) {
      setIsCreateProposalModalVisible(true)
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'There was a problem fetching template',
          level: 'error'
        }
      })
    }
  }

  const deleteTemplate = id => {
    const sweetAlertFactory = withSweetAlert(
      <div>
        <p style={{ color: 'red' }}>This action can&#39;t be reverted</p>
      </div>
    )
    sweetAlertFactory(
      {
        title: 'Are you sure you want to delete this template?',
        buttons: ['No', 'Yes'],
        icon: 'warning'
      },
      async () => {
        try {
          await makeDeleteRequest(`/v1/proposaltemplates/${id}`)
          const array = [...templates]
          const index = templates.findIndex(u => u.id === id)
          if (~index) {
            array.splice(index, 1)
            setTemplates(array)
            reduxDispatch({
              type: PUSH_NOTIFICATION,
              payload: {
                title: 'Template deleted successfuly',
                level: 'success'
              }
            })
          }
        } catch (e) {
          reduxDispatch({
            type: PUSH_NOTIFICATION,
            payload: {
              title: 'There was a problem deleting template',
              level: 'error'
            }
          })
        }
      },
      { cancelConfirmation: false }
    )
  }

  const installTemplate = async () => {
    const { id } = selectedPrefinedTemplate
    const result = await withLoadingLocks(
      makePostRequest('/v1/proposaltemplates/presignedtemplate', {
        id
      })
    )
    setTemplates([...templates, result])
    setSelectedPrefinedTemplate(null)
  }

  const cloneTemplate = async id => {
    try {
      const template = await withLoadingLocks(makePostRequest(`/v1/proposaltemplates/clone/${id}`))
      setTemplates([...templates, template])

      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'Template cloned successfully',
          level: 'success'
        }
      })
    } catch (e) {
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'There was a problem cloning template',
          level: 'error'
        }
      })
    }
  }

  return (
    <>
      <h5 className={s.type}>Install Proposal Templates from the iDeal Library</h5>
      <p className={s.description}>
        Click on the thumbnails below to see the templates in details and to install them.
      </p>
      <div className={s.thumbnailsWrapper}>
        <div className={s.list}>
          {predefinedTemplates.map(template => (
            <div
              onClick={() => setSelectedPrefinedTemplate(template)}
              key={template.id}
              className={s.thumbnail}
            >
              <img className={s.image} src={getBaseUrl(template.thumbnailUrl)} />
              <p className={s.title}>{template.title}</p>
            </div>
          ))}
        </div>
      </div>
      <AddButton
        onClick={() => setIsCreateProposalModalVisible(true)}
        className={s.greenBtn}
        cy="add_new_proposal_template_btn"
      >
        Add New Proposal Template
      </AddButton>
      {templates.length > 0 && (
        <List
          templates={templates}
          deleteTemplate={deleteTemplate}
          openEditModal={openEditModal}
          cloneTemplate={cloneTemplate}
        />
      )}
      {isCreateProposalModalVisible && (
        <CreateProposalModal
          isCreateProposalModalVisible={isCreateProposalModalVisible}
          setIsCreateProposalModalVisible={setIsCreateProposalModalVisible}
          saveTemplate={saveTemplate}
          setIsEditedTemplate={setIsEditedTemplate}
          template={isEditedTemplate}
          templateType="WORD"
        />
      )}
      {selectedPrefinedTemplate && (
        <InstallPredefinedTemplateModal
          selectedPrefinedTemplate={selectedPrefinedTemplate}
          setSelectedPrefinedTemplate={setSelectedPrefinedTemplate}
          installTemplate={installTemplate}
        />
      )}
    </>
  )
}

export default ProposalTemplates
